<script setup lang="ts">
import { computed, ref, nextTick, inject, toRef, type Component } from 'vue';
import type { IColumnDefinition } from './types';
import type { TableModel, RowModel } from './model';
import { useColumn, useCellFormatter } from './composables';
import { resolveEditorComponent } from './compile-template';
import {
  ItpTextEditor,
  ItpNumberEditor,
  ItpCheckboxEditor,
  ItpDateEditor,
  ItpLockEditor,
  ItpSelectEditor,
  ItpTimeEditor,
  ItpTextareaEditor,
} from './cell-types';

const props = defineProps<{
  row: RowModel;
  column: IColumnDefinition;
  editing?: boolean;
  autofocus?: boolean;
}>();

const model = inject<TableModel>('model');
if (!model) {
  throw new Error('ItpDataTableCell missing model injection');
}

const { columnClasses, columnStyle } = useColumn({
  column: props.column,
  options: model.options,
  editing: toRef(props, 'editing', false),
});

const errorMessage = computed(() => props.row.errors.value[props.column.field]);

const finalCellClasses = computed(() => {
  const classes = { ...columnClasses.value };

  classes['itp-datatable__table-cell'] = true;
  classes['position-relative'] = true;

  if (errorMessage.value && props.editing) {
    classes['has-error'] = true;
  }
  return classes;
});

function getDefaultEditorByType(type: string | undefined): Component {
  switch (type) {
    case 'number':
      return ItpNumberEditor;
    case 'date':
      return ItpDateEditor;
    case 'time':
      return ItpTimeEditor;
    case 'checkbox':
      return ItpCheckboxEditor;
    case 'lock':
      return ItpLockEditor;
    case 'select':
    case 'select2':
      return ItpSelectEditor;
    case 'textarea':
      return ItpTextareaEditor;
    case 'link':
    case 'template':
    case 'text':
    default:
      return ItpTextEditor;
  }
}

const { formatter: formatterComponent, isCustom: isCustomFormatter } =
  useCellFormatter(toRef(props, 'column'));

const resolvedCustomEditor = computed(() =>
  resolveEditorComponent(props.column.editor),
);

const editorComponent = computed<Component>(
  () => resolvedCustomEditor.value ?? getDefaultEditorByType(props.column.type),
);

const isCustomEditor = computed(() => !!resolvedCustomEditor.value);

const cellValue = computed(() => {
  return props.row.getValue(props.column.field);
});

const isEditable = computed(
  () =>
    props.column.editable !== false &&
    !props.column.readonly &&
    !props.column.systemColumn,
);

const showEditor = computed(() => {
  return props.editing && isEditable.value;
});

const globs = computed(() => model.options.globs || {});
const messages = computed(() => model.options.messages || {});

const onUpdateValue = (newValue: any) => {
  props.row.setValue(props.column.field, newValue);
};

const onEditorChange = (newValue: any) => {
  const field = props.column.field;
  model.emitCellEvent(`${field}:change`, {
    col: props.column,
    row: props.row,
    field,
    value: newValue,
  });
};

const editorRef = ref<any>(null);
const focus = () => {
  nextTick(() => {
    if (editorRef.value?.focus) {
      editorRef.value.focus();
    } else if (editorRef.value?.$el?.focus) {
      editorRef.value.$el.focus();
      const input = editorRef.value.$el.querySelector(
        'input, select, textarea',
      );
      if (input) {
        input.focus();
      }
    }
  });
};
defineExpose({ focus });
</script>

<template>
  <td
    :class="finalCellClasses"
    :style="columnStyle"
    :title="
      !showEditor && cellValue != null && typeof cellValue !== 'object'
        ? String(cellValue)
        : undefined
    "
  >
    <template v-if="showEditor">
      <component
        :is="editorComponent"
        v-if="isCustomEditor"
        :model-value="cellValue"
        :row="row.data"
        :row-model="row"
        :column="column"
        :table="model"
        :globs="globs"
        :messages="messages"
        :autofocus="autofocus"
        @update:model-value="onUpdateValue"
      />
      <component
        :is="editorComponent"
        v-else
        :model-value="cellValue"
        :row="row.data"
        :column="column"
        :autofocus="autofocus"
        @update:model-value="onUpdateValue"
        @change="onEditorChange"
      />
      <div v-if="errorMessage" class="invalid-feedback d-block">
        {{ errorMessage }}
      </div>
    </template>

    <template v-else>
      <component
        :is="formatterComponent"
        v-if="isCustomFormatter"
        :value="cellValue"
        :row="row.data"
        :column="column"
        :globs="globs"
        :messages="messages"
      />
      <component
        :is="formatterComponent"
        v-else
        :value="cellValue"
        :row="row.data"
        :column="column"
      />
    </template>
  </td>
</template>
